import axios, { AxiosError } from 'axios';
import {
  Publishable,
  PublishableStatus,
  PublishableType,
  PublishableWallOfLove,
} from '~/types/models/publishable';
import { PublishableResourceResponse } from '~/types/api/testimonials';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
  useQuery,
  UseQueryOptions,
} from 'react-query';
import { DefaultGetAllResponse } from '../rest';
import { PublishEditorState } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishEditorContext';

export const publishableApiUrl = '/api/v3/testimonials/publish';

export const queryKeys = {
  all: ['publishables'] as const,
  lists: () => [...queryKeys.all, 'list'] as const,
  list: (params: unknown) => [...queryKeys.lists(), params] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (uuid: string) => [...queryKeys.details(), uuid] as const,
};

export type PublishableRequestPayload = {
  tag_uuid: string | null;
  type: PublishableType;
  name: string;
  status: PublishableStatus;
  get_publishable_resources?: boolean;
  publishable_resources_uuids?: string[];
  metadata?: Publishable['metadata'];
};

export function parsePublishStateToMetadata(props?: PublishEditorState) {
  return {
    order: props?.order,
    tiles_per_page: props?.tilesPerPage,
    enable_max_height: props?.enableMaxHeight,
    allow_mobile_tile_restriction: props?.allowMobileTileRestriction,
    max_height: props?.maxHeight,
    display_order: props?.displayOrder,
    auto_slide: props?.autoSlide,
    allow_dynamic_tiles: props?.allowDynamicTiles,
    hide_name: props?.hideName,
    hide_title: props?.hideTitle,
    hide_profile_image: props?.hideProfileImage,
    hide_date: props?.hideDate,
    hide_carousel_indicator: props?.hideCarouselIndicator,
    hide_carousel_chevron: props?.hideCarouselChevron,
    style: {
      primary_color: props?.primaryColor,
      corner_radius: props?.cornerRadius,
      drop_shadow: props?.dropShadow,
      border: props?.border,
      ...(props?.border && {
        border_size: props?.borderSize,
        border_color: props?.borderColor,
      }),
      font_family: props?.fontFamily,
    },
  } as Publishable['metadata'];
}

export type GetPublishableResult =
  | [Publishable, { total_assets: number }]
  | [PublishableWallOfLove, undefined];

export async function getPublishable(
  uuid: string
): Promise<GetPublishableResult> {
  const res = await axios.get<PublishableResourceResponse>(
    `${publishableApiUrl}/${uuid}`
  );

  if ('metadata' in res.data) {
    return [res.data.data, res.data.metadata];
  }

  return [res.data.data, undefined];
}

export interface UseGetPublishableParams {
  uuid: string;
}

export const usePublishable = (
  params: UseGetPublishableParams,
  options?: UseQueryOptions<GetPublishableResult, AxiosError>
) => {
  return useQuery<GetPublishableResult, AxiosError>(
    queryKeys.detail(params.uuid),
    () => getPublishable(params.uuid),
    options
  );
};

export async function createPublishable(payload: PublishableRequestPayload) {
  const res = await axios.post<PublishableResourceResponse>(
    publishableApiUrl,
    payload
  );
  return res.data.data;
}

type UpdatePublishableRequestPayload = {
  uuid: string;
  context?: string;
} & Partial<PublishableRequestPayload>;

export async function updatePublishable({
  uuid,
  ...payload
}: UpdatePublishableRequestPayload) {
  const res = await axios.put<PublishableResourceResponse>(
    `${publishableApiUrl}/${uuid}`,
    payload
  );
  return res.data.data;
}

export interface DeletePublishableArgs {
  uuid: string;
  context?: string;
}

export function deletePublishable({ uuid, context }: DeletePublishableArgs) {
  return axios.delete<PublishableResourceResponse>(
    `${publishableApiUrl}/${uuid}`,
    {
      params: { context },
    }
  );
}

export const useCreatePublishable = (
  options?: UseMutationOptions<
    Publishable,
    AxiosError,
    PublishableRequestPayload
  >
) => {
  const queryClient = useQueryClient();
  return useMutation(createPublishable, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.lists());
    },
    ...options,
  });
};

export const useUpdatePublishable = (
  options?: UseMutationOptions<
    Publishable,
    AxiosError,
    UpdatePublishableRequestPayload
  >
) => {
  const queryClient = useQueryClient();
  return useMutation(updatePublishable, {
    onSuccess: (publishableResponse) => {
      queryClient.invalidateQueries(queryKeys.detail(publishableResponse.uuid));
    },
    ...options,
  });
};

export interface GetPublishablesParams {
  publishable_type?: PublishableType;
}

export async function getPublishables(params?: GetPublishablesParams) {
  const res = await axios.get<DefaultGetAllResponse<Publishable>>(
    publishableApiUrl,
    {
      params,
    }
  );

  return res.data;
}

export function usePublishables(
  params?: GetPublishablesParams,
  options?: UseQueryOptions<DefaultGetAllResponse<Publishable>, AxiosError>
) {
  return useQuery<DefaultGetAllResponse<Publishable>, AxiosError>(
    queryKeys.list(params),
    () => getPublishables(params),
    options
  );
}
